import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  Database,
  Eye,
  Share2,
  Lock,
  Clock,
  UserCheck,
  Cookie,
  Users,
  RefreshCw,
  Mail,
  ArrowLeft,
} from "lucide-react";

const EFFECTIVE_DATE = "March 18, 2026";
const COMPANY_NAME = "VedaLogic";
const CONTACT_EMAIL = "support@payprism.io";

interface PolicySectionProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}

function PolicySection({ icon: Icon, title, children }: PolicySectionProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="h-4 w-4 text-primary" />
          </div>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground leading-relaxed space-y-3">
        {children}
      </CardContent>
    </Card>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-4xl mx-auto flex items-center justify-between h-14 px-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <span className="font-semibold">{COMPANY_NAME}</span>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <a href="/" className="flex items-center gap-1.5">
              <ArrowLeft className="h-4 w-4" />
              Back
            </a>
          </Button>
        </div>
      </header>

      <div className="p-6 space-y-8 max-w-4xl mx-auto pb-16">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-display font-bold tracking-tight">Privacy Policy</h1>
            <Badge variant="premium">Legal</Badge>
          </div>
          <p className="text-muted-foreground">
            Effective date: {EFFECTIVE_DATE}
          </p>
        </div>

      {/* Introduction */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <p className="text-sm leading-relaxed">
            At {COMPANY_NAME}, we are committed to protecting your privacy and ensuring the security
            of your personal information. This Privacy Policy explains how we collect, use, disclose,
            and safeguard your information when you use our card management platform and related
            services. By accessing or using our services, you agree to the terms outlined in this
            policy.
          </p>
        </CardContent>
      </Card>

      {/* Sections */}
      <div className="space-y-6">
        <PolicySection icon={Database} title="1. Information We Collect">
          <p>We collect the following categories of information:</p>
          <div className="space-y-2 pl-4">
            <div>
              <p className="font-medium text-foreground">Account Information</p>
              <p>Name, email address, phone number, organization details, and role-based credentials used to access the platform.</p>
            </div>
            <div>
              <p className="font-medium text-foreground">Transaction Data</p>
              <p>Card transaction records, wallet balances, payment history, and associated financial data processed through the platform.</p>
            </div>
            <div>
              <p className="font-medium text-foreground">Usage Data</p>
              <p>IP addresses, browser type, device information, access timestamps, pages viewed, and interactions with platform features.</p>
            </div>
            <div>
              <p className="font-medium text-foreground">Customer Data</p>
              <p>Information about end customers managed through the platform, including names, contact details, and card program enrollment data.</p>
            </div>
            <div>
              <p className="font-medium text-foreground">Communication Data</p>
              <p>Email correspondence, support tickets, and feedback submitted through the platform.</p>
            </div>
          </div>
        </PolicySection>

        <PolicySection icon={Eye} title="2. How We Use Your Information">
          <p>We use collected information for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Providing, operating, and maintaining the card management platform</li>
            <li>Processing card transactions, wallet operations, and financial reporting</li>
            <li>Authenticating users and enforcing role-based access controls</li>
            <li>Sending transactional emails, security alerts, and service notifications</li>
            <li>Generating analytics, audit logs, and compliance reports</li>
            <li>Improving platform performance, features, and user experience</li>
            <li>Detecting and preventing fraud, unauthorized access, and security threats</li>
            <li>Fulfilling legal and regulatory obligations</li>
          </ul>
        </PolicySection>

        <PolicySection icon={Share2} title="3. Data Sharing & Disclosure">
          <p>We do not sell your personal information. We may share data with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><span className="font-medium text-foreground">Service Providers:</span> Third-party vendors who assist in payment processing, email delivery (e.g., Resend), cloud hosting (e.g., Vercel), and data storage</li>
            <li><span className="font-medium text-foreground">Card Network Partners:</span> Payment processors and card issuing partners necessary for card program operations</li>
            <li><span className="font-medium text-foreground">Organization Hierarchy:</span> Data may be shared within your organizational structure (organization, business units) as configured by your administrators</li>
            <li><span className="font-medium text-foreground">Legal Requirements:</span> When required by law, regulation, legal process, or governmental request</li>
            <li><span className="font-medium text-foreground">Business Transfers:</span> In connection with a merger, acquisition, or sale of assets</li>
          </ul>
        </PolicySection>

        <PolicySection icon={Lock} title="4. Data Security">
          <p>We implement industry-standard security measures to protect your information:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Encryption of data in transit (TLS/SSL) and at rest</li>
            <li>Role-based access controls (SUPER, ORGANIZATION, BUSINESS_UNIT, FINANCE roles)</li>
            <li>Session management with secure authentication tokens</li>
            <li>Comprehensive audit logging of all administrative actions</li>
            <li>Regular security assessments and vulnerability testing</li>
            <li>API key management with scoped permissions for developer integrations</li>
            <li>Two-factor authentication support for sensitive operations</li>
          </ul>
          <p>
            While we strive to use commercially acceptable means to protect your personal
            information, no method of electronic transmission or storage is 100% secure, and we
            cannot guarantee absolute security.
          </p>
        </PolicySection>

        <PolicySection icon={Clock} title="5. Data Retention">
          <p>We retain your information for as long as necessary to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Maintain your active account and provide our services</li>
            <li>Comply with legal, regulatory, and financial reporting obligations</li>
            <li>Resolve disputes and enforce our agreements</li>
            <li>Maintain audit trails as required by applicable regulations</li>
          </ul>
          <p>
            Transaction records and audit logs may be retained for a minimum period as required by
            financial regulations. When data is no longer needed, it is securely deleted or
            anonymized.
          </p>
        </PolicySection>

        <PolicySection icon={UserCheck} title="6. Your Rights">
          <p>
            Depending on your jurisdiction, you may have the following rights regarding your
            personal data:
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              { right: "Access", desc: "Request a copy of the personal data we hold about you" },
              { right: "Rectification", desc: "Request correction of inaccurate or incomplete data" },
              { right: "Erasure", desc: "Request deletion of your personal data (\"right to be forgotten\")" },
              { right: "Portability", desc: "Receive your data in a structured, machine-readable format" },
              { right: "Restriction", desc: "Request limitation of processing of your personal data" },
              { right: "Objection", desc: "Object to processing based on legitimate interests" },
              { right: "Withdraw Consent", desc: "Withdraw previously given consent at any time" },
              { right: "Non-Discrimination", desc: "Exercise your rights without discriminatory treatment" },
            ].map(({ right, desc }) => (
              <div key={right} className="rounded-lg border p-3">
                <p className="font-medium text-foreground text-xs">{right}</p>
                <p className="text-xs mt-0.5">{desc}</p>
              </div>
            ))}
          </div>
          <p>
            To exercise any of these rights, please contact us at{" "}
            <span className="font-medium text-foreground">{CONTACT_EMAIL}</span>. We will respond
            to your request within 30 days.
          </p>
        </PolicySection>

        <PolicySection icon={Cookie} title="7. Cookies & Tracking">
          <p>We use cookies and similar technologies to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><span className="font-medium text-foreground">Essential Cookies:</span> Maintain authentication sessions, remember preferences, and ensure platform functionality</li>
            <li><span className="font-medium text-foreground">Analytics:</span> Understand platform usage patterns to improve our services</li>
            <li><span className="font-medium text-foreground">Security:</span> Detect suspicious activity and protect against unauthorized access</li>
          </ul>
          <p>
            You can control cookie preferences through your browser settings. Disabling essential
            cookies may affect platform functionality.
          </p>
        </PolicySection>

        <PolicySection icon={Users} title="8. Children's Privacy">
          <p>
            Our platform is designed for business use and is not intended for individuals under the
            age of 18. We do not knowingly collect personal information from children. If we become
            aware that we have collected data from a child under 18, we will take steps to delete
            such information promptly.
          </p>
        </PolicySection>

        <PolicySection icon={RefreshCw} title="9. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            technologies, legal requirements, or other factors. We will notify you of any material
            changes by posting the updated policy on this page and updating the effective date. Your
            continued use of the platform after changes are posted constitutes acceptance of the
            revised policy.
          </p>
        </PolicySection>

        <PolicySection icon={Mail} title="10. Contact Us">
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or our data
            practices, please contact us:
          </p>
          <div className="rounded-lg border p-4 space-y-1">
            <p className="font-medium text-foreground">{COMPANY_NAME} — Privacy Team</p>
            <p>Email: <span className="font-medium text-foreground">{CONTACT_EMAIL}</span></p>
          </div>
        </PolicySection>
      </div>
      </div>
    </div>
  );
}
